import React, { useMemo, useEffect } from 'react';
import RMTable from 'components/customComponents/Controls/RMTable';
import RMCheckBox from 'components/customComponents/Controls/tableSelectionCheckBox';
import { SaveProfileDTO } from 'business/models/Models';
import HeaderBar from '../../CrystalContainer/HeaderBar';
import { createDefaultSaveProfileDTO } from '../utils';
import { ImageSettingChecks } from '../constants';
import { CaptureProfileType, ImageSettingsRowInfo } from '../types';
import { useSelectedRobotId } from 'redux/hooks/imagingSetup';

interface ImageSettingsPanelProps {
  setEnableHeaderButtons: React.Dispatch<React.SetStateAction<boolean>>;
  cpData: CaptureProfileType;
  setCpData: React.Dispatch<React.SetStateAction<CaptureProfileType>>;
  enableAll: boolean;
  setEnableAll: React.Dispatch<React.SetStateAction<boolean>>;
  importFlAll: boolean;
  setImportFlAll: React.Dispatch<React.SetStateAction<boolean>>;
  imageSettingErrors: ImageSettingsRowInfo;
  selectedCps: Array<number>;
  setSelectedCps: React.Dispatch<React.SetStateAction<Array<number>>>;
  selectedRows: Array<number>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Array<number>>>;
  deletedImagingSettings: Array<number>;
  setDeletedImagingSettings: React.Dispatch<React.SetStateAction<Array<number>>>;
}

const ImageSettingsPanel = (props: ImageSettingsPanelProps) => {
  const {
    setEnableHeaderButtons,
    cpData,
    setCpData,
    enableAll,
    setEnableAll,
    importFlAll,
    setImportFlAll,
    imageSettingErrors,
    selectedCps,
    setSelectedCps,
    selectedRows,
    setSelectedRows,
    deletedImagingSettings,
    setDeletedImagingSettings,
  } = props;

  const robotId = useSelectedRobotId();

  const toggleImageSettingsColumn = (e, column: ImageSettingChecks) => {
    const newData = { ...cpData };
    if (column === ImageSettingChecks.enabled) {
      Object.keys(newData).forEach((key) => {
        const updatedCp = { ...newData[key], enabled: !enableAll };
        newData[key] = updatedCp;
      });
      setEnableAll(!enableAll);
    } else if (column === ImageSettingChecks.importFl) {
      Object.keys(newData).forEach((key) => {
        const updatedCp = { ...newData[key], importFl: !importFlAll };
        newData[key] = updatedCp;
      });
      setImportFlAll(!importFlAll);
    }
    setCpData(newData);
    setEnableHeaderButtons(true);
  };
  const checkIsIndeterminate = (column: ImageSettingChecks) => {
    const cpValues = Object.values(cpData);
    if (column === ImageSettingChecks.enabled) {
      return (
        cpValues.some((cp: any) => cp.enabled === true) &&
        cpValues.some((cp: any) => cp.enabled === false)
      );
    } else if (column === ImageSettingChecks.importFl) {
      return (
        cpValues.some((cp: any) => cp.importFl === true) &&
        cpValues.some((cp: any) => cp.importFl === false)
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <div id="imagesettingspanel-imagingsettingsname-wrapper" className="">
            <span id="imagesettingspanel-imagingsettingsname-title">
              Imaging Settings Name
            </span>
          </div>
        ),
        accessor: 'name',
        className: 'image-settings-name',
        editOptions: { inputType: 'text' },
      },
      {
        Header: (
          <div id="imagesettingspanel-importfl-wrapper" className={'label-with-checkbox'}>
            <span id="imagesettingspanel-importfl-title">Import Fl</span>
            <div id="imagesettingspanel-importfl-checkbox-wrapper">
              <RMCheckBox
                onChange={(e) => {
                  toggleImageSettingsColumn(e, ImageSettingChecks.importFl);
                }}
                checked={importFlAll}
                indeterminate={checkIsIndeterminate(ImageSettingChecks.importFl)}
              />
            </div>
          </div>
        ),
        accessor: 'importFl',
        className: 'image-settings-import-fl !items-center',
      },
      {
        Header: (
          <div id="imagesettingspanel-enabled-wrapper" className={'label-with-checkbox'}>
            <span id="imagesettingspanel-enabled-title">Enable</span>
            <div id="imagesettingspanel-enabled-checkbox-wrapper">
              <RMCheckBox
                onChange={(e) => {
                  toggleImageSettingsColumn(e, ImageSettingChecks.enabled);
                }}
                checked={enableAll}
                indeterminate={checkIsIndeterminate(ImageSettingChecks.enabled)}
              />
            </div>
          </div>
        ),
        accessor: 'enabled',
        className: 'image-settings-enable !items-center',
      },
    ],
    [enableAll, importFlAll, cpData],
  );

  const onAddClick = () => {
    const newData = { ...cpData };
    const newDto = createDefaultSaveProfileDTO(robotId);
    newData[`${newDto.id}`] = newDto;
    setCpData(newData);
    setEnableHeaderButtons(true);
  };

  const onDeleteClick = () => {
    // eslint-disable-next-line
    const { [selectedCps[0]]: removedCp, ...newData } = cpData;
    setCpData(newData);
    if (selectedCps.length > 0) {
      setDeletedImagingSettings([...deletedImagingSettings, selectedCps[0]]);
    }
    setSelectedCps([]);
    setSelectedRows([]);
    setEnableHeaderButtons(true);
  };

  const handleOnRowCLick = (row) => {
    setSelectedRows([row.index]);
    setSelectedCps([row.original.id]);
  };

  const onCellChange = (e, id) => {
    const { value } = e.target;
    setEnableHeaderButtons(true);
    const newCpData = { ...cpData };
    newCpData[id] = new SaveProfileDTO(newCpData[id]);
    newCpData[id].name = value;
    setCpData({ ...newCpData });
  };

  const checkEnableAll = (cpData: CaptureProfileType) => {
    if (Object.values(cpData).every((cp) => cp.enabled === true)) {
      setEnableAll(true);
    }
  };
  const checkImportFlAll = (cpData: CaptureProfileType) => {
    if (Object.values(cpData).every((cp) => cp.importFl === true)) {
      setImportFlAll(true);
    }
  };
  const invertImportFlRow = (e, id) => {
    const newData = { ...cpData };
    const updatedCp = new SaveProfileDTO();
    Object.assign(updatedCp, newData[id]);
    updatedCp.importFl = !updatedCp.importFl;
    newData[id] = updatedCp;
    setCpData(newData);
    setEnableHeaderButtons(true);
    if (newData[id].importFl === false) {
      setImportFlAll(false);
    } else {
      checkImportFlAll(newData);
    }
  };
  const invertEnabledRow = (e, id) => {
    const newData = { ...cpData };
    const updatedCp = new SaveProfileDTO();
    Object.assign(updatedCp, newData[id]);
    updatedCp.enabled = !updatedCp.enabled;
    newData[id] = updatedCp;
    setCpData(newData);
    setEnableHeaderButtons(true);
    checkEnableAll(newData);
    if (newData[id].enabled === false) {
      setEnableAll(false);
    } else {
      checkEnableAll(newData);
    }
  };
  const tableRows = () => {
    return Object.values(cpData).map((cp: any) => {
      return {
        id: cp.id,
        name: (
          <input
            id={`imagesettingspanel-imagingsettingsname-${cp.id}-input`}
            type="text"
            key={cp.id}
            name="name"
            value={cp.name}
            className="!border-0 bg-transparent p-2 w-full"
            onChange={(e) => onCellChange(e, cp.id)}
          />
        ),
        importFl: (
          <RMCheckBox
            onChange={(e) => invertImportFlRow(e, cp.id)}
            checked={cp.importFl}
          />
        ),
        enabled: (
          <RMCheckBox onChange={(e) => invertEnabledRow(e, cp.id)} checked={cp.enabled} />
        ),
      };
    });
  };

  useEffect(() => {
    if (cpData && selectedRows.length) {
      setSelectedCps([Object.values(cpData)[selectedRows[0]]?.id]);
    }
  }, [selectedRows]);

  return (
    <div id="imagesettingspanel-wrapper" className="w-full h-[85%]">
      <HeaderBar
        title="Imaging Settings"
        onAdd={onAddClick}
        onDelete={onDeleteClick}
        enableEditDelete={
          selectedRows.length > 0 ? !cpData[selectedCps[0]]?.inUse ?? true : false
        }
      />
      <RMTable
        columns={columns}
        data={tableRows()}
        tableClass={'w-100'}
        selectedIds={selectedRows}
        onRowClicked={handleOnRowCLick}
        rowExtraInfo={imageSettingErrors}
      />
    </div>
  );
};

export default ImageSettingsPanel;
